import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { SpinnerGap, FloppyDisk, Check, Sparkle } from "@phosphor-icons/react";
import { ProfileInput } from "./shared";
import { BrandPaletteSection } from "./BrandPaletteSection";

export function PersonalizationTab({ profile, loadingProfile, updateField, saveProfile, savingProfile, activeBusiness }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="personalization-tab">
      {!loadingProfile && profile && <ProfileStrengthMeter profile={profile} />}

      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-6">
        <h2 className="font-['Cormorant_Garamond'] text-lg font-semibold text-[#FF4400] mb-1">Brand Identity</h2>
        <p className="text-sm text-[var(--text-muted)] mb-6">
          This information is injected into every AI prompt so the output sounds like you — not generic AI.
        </p>

        {loadingProfile ? (
          <div className="flex justify-center py-12"><SpinnerGap size={28} className="animate-spin text-[#FF4400]" /></div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <ProfileInput label="Your Name" value={profile?.your_name || ""} onChange={(v) => updateField("your_name", v)} placeholder="Your full name" />
              <ProfileInput label="Title / Role" value={profile?.your_title || ""} onChange={(v) => updateField("your_title", v)} placeholder="e.g., Marketing Consultant, Agency Owner, Freelance Strategist" />
            </div>
            <ProfileInput label="Company / Brand" value={profile?.your_company || ""} onChange={(v) => updateField("your_company", v)} placeholder="Your company or brand name" />
            <ProfileInput label="Who You Serve" sub="Describe your ideal client / audience in a few sentences." value={profile?.who_you_serve || ""} onChange={(v) => updateField("who_you_serve", v)} placeholder="e.g. Small business owners looking to scale, executives navigating change..." textarea />
            <ProfileInput label="Positioning Statement" sub="What you do differently and why people come to you." value={profile?.positioning_statement || ""} onChange={(v) => updateField("positioning_statement", v)} placeholder="e.g. I help organizations build systems that drive predictable growth..." textarea />
            <ProfileInput label="Signature Frameworks" sub="Named methods, processes, or mental models you reference." value={profile?.signature_frameworks || ""} onChange={(v) => updateField("signature_frameworks", v)} placeholder="e.g. The Growth Blueprint, The 5-Step Clarity Method..." textarea />
            <ProfileInput label="Differentiators" sub="What makes your perspective or approach different from others?" value={profile?.differentiators || ""} onChange={(v) => updateField("differentiators", v)} placeholder="Anti-vanity metrics stance, practitioner-first not theory-first..." textarea />
            <ProfileInput label="Content Pillars" sub="Top 3-5 themes you always talk about." value={profile?.content_pillars || ""} onChange={(v) => updateField("content_pillars", v)} placeholder="e.g. Leadership, growth strategy, client experience, personal branding..." textarea />

            <div className="flex justify-end pt-2">
              <Button onClick={saveProfile} disabled={savingProfile} className="bg-[#FF4400] hover:bg-[#FF6633] text-white gap-2" data-testid="save-identity-btn">
                {savingProfile ? <SpinnerGap size={16} className="animate-spin" /> : <FloppyDisk size={16} weight="bold" />}
                Save Brand Identity
              </Button>
            </div>
          </div>
        )}
      </div>

      <BrandPaletteSection />

      <BrandVoiceSection profile={profile} loadingProfile={loadingProfile} updateField={updateField} saveProfile={saveProfile} savingProfile={savingProfile} activeBusiness={activeBusiness} />
    </motion.div>
  );
}

function BrandVoiceSection({ profile, loadingProfile, updateField, saveProfile, savingProfile, activeBusiness }) {
  const [voiceTab, setVoiceTab] = useState(
    profile?.voice_profile?.extracted_at ? "ai" : "ai"
  );
  const hasVoiceProfile = !!profile?.voice_profile?.extracted_at;
  const vp = profile?.voice_profile || {};

  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg overflow-hidden" data-testid="brand-voice-section">
      <div className="p-6 pb-0">
        <h2 className="font-['Cormorant_Garamond'] text-lg font-semibold text-[#FF4400] mb-1">Brand Voice</h2>
        <p className="text-sm text-[var(--text-muted)] mb-4">
          How your content sounds. Use AI to extract your voice from examples, or write it manually.
        </p>

        <div className="flex gap-1 border-b border-[var(--border)]">
          <button
            onClick={() => setVoiceTab("ai")}
            className={`px-4 py-2.5 text-xs font-medium transition-colors relative ${voiceTab === "ai" ? "text-[#FF4400]" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"}`}
            data-testid="voice-tab-ai"
          >
            <Sparkle size={14} className="inline mr-1.5" weight={voiceTab === "ai" ? "fill" : "regular"} />
            AI Voice Capture
            {voiceTab === "ai" && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#FF4400] rounded-full" />}
          </button>
          <button
            onClick={() => setVoiceTab("manual")}
            className={`px-4 py-2.5 text-xs font-medium transition-colors relative ${voiceTab === "manual" ? "text-[#FF4400]" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"}`}
            data-testid="voice-tab-manual"
          >
            Manual
            {voiceTab === "manual" && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#FF4400] rounded-full" />}
          </button>
        </div>
      </div>

      <div className="p-6">
        {voiceTab === "ai" ? (
          <div className="space-y-4">
            {hasVoiceProfile ? (
              <>
                <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-4 space-y-3" data-testid="voice-profile-active">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Check size={16} className="text-emerald-400" weight="bold" />
                      <span className="text-sm font-medium text-emerald-400">Voice Profile Active</span>
                    </div>
                    <span className="text-[10px] text-[var(--text-muted)]">
                      Captured {new Date(vp.extracted_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-[var(--text-secondary)] leading-relaxed italic">"{vp.voice_summary}"</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(vp.tone_attributes || []).slice(0, 5).map((attr) => (
                      <span key={`attr-${attr}`} className="px-2 py-0.5 text-[10px] rounded-full bg-[#FF4400]/10 text-[#FF4400] border border-[#FF4400]/20">{attr}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
                    <span>Confidence: {vp.confidence_score}/10</span>
                    <span className="text-[var(--border)]">|</span>
                    <span>{(vp.personality_traits || []).join(", ")}</span>
                  </div>
                </div>
                <div className="flex justify-end gap-4">
                  <a href={`/brand/voice-history?business_id=${activeBusiness?.business_id || ""}`} className="text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] font-medium transition-colors" data-testid="view-voice-history-link">
                    View Voice History
                  </a>
                  <a href={`/brand/voice-wizard?business_id=${activeBusiness?.business_id || ""}`} className="text-sm text-[#FF4400] hover:text-[#FF6633] font-medium transition-colors" data-testid="recapture-voice-link">
                    Re-capture Voice
                  </a>
                </div>
              </>
            ) : (
              <div className="text-center py-8 space-y-4" data-testid="no-voice-profile">
                <div className="w-14 h-14 rounded-2xl bg-[#FF4400]/10 flex items-center justify-center mx-auto">
                  <Sparkle size={28} className="text-[#FF4400]" weight="duotone" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-[var(--text-primary)]">No voice profile yet</h3>
                  <p className="text-sm text-[var(--text-muted)] mt-1 max-w-md mx-auto">
                    Paste 2-3 examples of your best content and let AI extract your unique voice profile. Takes about 2 minutes.
                  </p>
                </div>
                <a href={`/brand/voice-wizard?business_id=${activeBusiness?.business_id || ""}`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#FF4400] hover:bg-[#FF6633] text-white text-sm font-semibold transition-colors" data-testid="capture-voice-cta">
                  <Sparkle size={16} /> Capture Your Voice
                </a>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <ProfileInput
              label="Brand Voice"
              sub="Describe how your content should sound. This text is used directly in generation prompts."
              value={profile?.brand_voice || ""}
              onChange={(v) => updateField("brand_voice", v)}
              placeholder="e.g. Write in a confident, no-nonsense tone. Use short sentences. Avoid corporate jargon. Sound like a practitioner who's been in the trenches, not a consultant reading from a textbook."
              textarea
            />
            {hasVoiceProfile && (
              <p className="text-xs text-[var(--text-muted)]">
                Note: When an AI voice profile is active, the voice summary is used as the primary voice instruction. Manual text is used as a fallback.
              </p>
            )}
            <div className="flex justify-end pt-2">
              <Button onClick={saveProfile} disabled={savingProfile} className="bg-[#FF4400] hover:bg-[#FF6633] text-white gap-2" data-testid="save-manual-voice-btn">
                {savingProfile ? <SpinnerGap size={16} className="animate-spin" /> : <FloppyDisk size={16} weight="bold" />}
                Save Brand Voice
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ProfileStrengthMeter({ profile }) {
  const hasFired = useRef(false);
  const fields = [
    { key: "your_name", label: "Name", filled: !!profile?.your_name },
    { key: "your_title", label: "Title / Role", filled: !!profile?.your_title },
    { key: "who_you_serve", label: "Who You Serve", filled: !!profile?.who_you_serve },
    { key: "positioning_statement", label: "Positioning Statement", filled: !!profile?.positioning_statement },
    { key: "differentiators", label: "Differentiators", filled: !!profile?.differentiators },
    { key: "content_pillars", label: "Content Pillars", filled: !!profile?.content_pillars },
    { key: "signature_frameworks", label: "Signature Frameworks", filled: !!profile?.signature_frameworks },
    { key: "voice_profile", label: "AI Voice Profile", filled: !!profile?.voice_profile?.extracted_at },
  ];

  const filledCount = fields.filter(f => f.filled).length;
  const pct = Math.round((filledCount / fields.length) * 100);
  const barColor = pct === 100 ? "#22C55E" : pct >= 60 ? "#FF4400" : "#F59E0B";
  const label = pct === 100 ? "Complete" : pct >= 60 ? "Strong" : pct >= 30 ? "Getting there" : "Just started";

  useEffect(() => {
    if (pct === 100 && !hasFired.current) {
      const alreadyCelebrated = localStorage.getItem("profile_strength_celebrated");
      if (!alreadyCelebrated) {
        hasFired.current = true;
        localStorage.setItem("profile_strength_celebrated", "true");
        const end = Date.now() + 1500;
        const frame = () => {
          confetti({ particleCount: 3, angle: 60, spread: 55, origin: { x: 0, y: 0.7 }, colors: ["#FF4400", "#22C55E", "#F59E0B"] });
          confetti({ particleCount: 3, angle: 120, spread: 55, origin: { x: 1, y: 0.7 }, colors: ["#FF4400", "#22C55E", "#F59E0B"] });
          if (Date.now() < end) requestAnimationFrame(frame);
        };
        frame();
        toast.success("Profile complete — your content just got a lot sharper!");
      }
    }
  }, [pct]);

  if (pct === 100 && localStorage.getItem("profile_strength_celebrated")) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg px-5 py-3 flex items-center gap-3"
        data-testid="profile-strength-complete"
      >
        <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
          <Check size={14} weight="bold" className="text-emerald-400" />
        </div>
        <span className="text-sm font-medium text-emerald-400">Profile complete — all fields filled</span>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-5"
      data-testid="profile-strength-meter"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-[var(--text-primary)]">Profile Strength</span>
          <span
            className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
            style={{ backgroundColor: `${barColor}15`, color: barColor, border: `1px solid ${barColor}30` }}
            data-testid="profile-strength-label"
          >
            {label} — {pct}%
          </span>
        </div>
        <span className="text-xs text-[var(--text-muted)] font-['JetBrains_Mono']">{filledCount}/{fields.length}</span>
      </div>

      <div className="h-2 rounded-full bg-[var(--bg-base)] overflow-hidden mb-4">
        <motion.div
          className="h-full rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          style={{ backgroundColor: barColor }}
          data-testid="profile-strength-bar"
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {fields.map(f => (
          <div key={f.key} className="flex items-center gap-1.5" data-testid={`strength-field-${f.key}`}>
            <div className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${
              f.filled ? "bg-emerald-500/20 text-emerald-400" : "bg-[var(--bg-base)] border border-[var(--border)] text-transparent"
            }`}>
              {f.filled && <Check size={10} weight="bold" />}
            </div>
            <span className={`text-xs truncate ${f.filled ? "text-[var(--text-secondary)]" : "text-[var(--text-muted)]"}`}>{f.label}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
}
